// Layouts
import {
    fetchEntityListRequest,
    fetchEntityHierarchyRequest,
} from 'containers/MerchantOnboard/redux/merchantOnBoard.actions';

import moment from 'moment';
import React, {
    useMemo,
    useState,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { components } from 'react-select';
import { fetchAuditTrailStaticRequest } from '../../redux/common/staticData/staticData.actions';
import {
    Table,
    FormElems,
    Popup,
    ViewDownloadStatus,
    SettlementForm,
    TableSearch,
    CustomSelect,
    TableFilter,
    OdsCard,
    ErrorCard,
} from '../../components';
import {
    dateOptionsSettlement,
    ENTITY_TYPE,
    TRANSACTION_TYPE,
} from '../../utils/constants/commonValues';
import {
    formatFormDataOptions,
    getFormattedDate,
    getFormButtonText,
} from '../../utils/helperMethods';
import {
    generateReportRequest,
    generateReportError,
    resetTxnState,
} from '../Transactions/redux/transactions.actions';
import { PAYMENT_MODE } from '../Transactions/constant';
import DownloadFile from './DownloadFile';
import {
    fetchSettlementRequest,
    fetchSettlementMetricsRequest,
    retrySettlementRequest,
    retriggerSettlementRequest,
    fetchAvailableBalanceRequest,
    getMerchantSectionInfoRequest,
    fetchNetSettlementRequest,
} from './redux/settlement.actions';
import DateSection from './DateFilter';
import CurrentDaySummary from './CurrentDaySummary';

// Libraries

// Styles
import './settlement.scss';
import { UNIFIED_MSR } from './constant';

const filterFieldList = [
    {
        fieldType: 'date',
        label: 'Settlement Start/End Date',
        name: 'createdOn',
        props: {
            startDate: moment()
                .endOf('day')
                .subtract(1, 'days'),
            endDate: moment().endOf('day'),
            maximumNights: 30,
            notResetDate: true,
        },
    },
    {
        fieldType: 'search-select',
        label: 'Aggregator Name',
        name: 'aggregatorId',
        props: {
            data: [],
            label: 'Select Aggregator',
            labelField: 'name',
            valueField: 'entityCode',
            entityType: 'AGGREGATOR',
        },
    },
    {
        fieldType: 'multi-select',
        label: 'Merchant Name',
        name: 'merchantName',
        props: {
            data: [],
            label: 'Select Merchant',
            labelField: 'name',
            valueField: 'entityCode',
            entityType: 'MERCHANT_NAME',
        },
    },
    {
        fieldType: 'multi-select',
        label: 'Merchant ID',
        name: 'merchantId',
        props: {
            data: [],
            label: 'Select Merchant',
            labelField: 'entityCode',
            valueField: 'entityCode',
            entityType: 'MERCHANT_ID',
        },
    },
    {
        fieldType: 'parallel-input',
        label: 'Amount',
        fieldA: {
            name: 'minAmt',
            props: {
                placeholder: 'Min. Amount',
                type: 'number',
            },
        },
        fieldB: {
            name: 'maxAmt',
            props: {
                placeholder: 'Max. Amount',
                type: 'number',
            },
        },
    },
    {
        fieldType: 'multi-select',
        label: 'Settlement State',
        name: 'settlementStates',
        props: {
            data: [
                {
                    name: 'Initiated',
                    value: 'INITIATED',
                },
                {
                    name: 'Disbursed',
                    value: 'DISBURSED',
                },
                {
                    name: 'Settled',
                    value: 'SETTLED',
                },
            ],
            label: 'Select Settlement State',
            labelField: 'name',
            valueField: 'value',
        },
    },
    {
        fieldType: 'select',
        label: 'Settlement Mode',
        name: 'settlementMode',
        props: {
            data: [
                {
                    name: 'Cyclic',
                    value: 'CYCLIC',
                },
                {
                    name: 'Instant',
                    value: 'INSTANT',
                },
                {
                    name: 'ODS',
                    value: 'ODS',
                },
                {
                    name: 'Split Settlement',
                    name: 'Split Settlement',
                    value: 'EDI',
                },
                {
                    name: 'Split Settlement Partner Report',
                    value: 'EDI_TRANSACTION',
                },
            ],
            label: 'Select Settlement Mode',
            labelField: 'name',
            valueField: 'value',
        },
    },
];

const searchKeyList = [
    {
        name: 'FCPG TXN ID',
        value: 'TRANSACTION_ID',
    },
    {
        name: 'Merchant TXN ID',
        value: 'ORDER_ID',
    },
    {
        name: 'Settlement ID',
        value: 'SETTLEMENT_ID',
    },
    {
        name: 'NEFT ID',
        value: 'NEFT_ID',
    },
];

const formatOptions = merchantData => {
    if (!isEmpty(merchantData)) {
        const formatMerchant = merchantData.map(item => {
            return {
                label: item.name,
                value: item.entityCode,
                checked: false,
                type: 'merchant',
            };
        });

        return formatMerchant;
    }
    return [];
};

const formatOptionsID = merchantData => {
    if (!isEmpty(merchantData)) {
        const formatMerchant = merchantData.map(item => {
            return {
                label: item.entityCode,
                value: item.entityCode,
                checked: false,
                type: 'merchant',
            };
        });

        return formatMerchant;
    }
    return [];
};

const ValueContainer = ({ children, ...props }) => {
    const { getValue, hasValue, options } = props;
    const newChildren = [...children];
    const nbValues = getValue().filter(item => item.value !== 'all').length;
    const type = options[0]?.type;
    if (type === 'merchant') {
        newChildren[0] = `${nbValues} Merchants Selected`;
    } else {
        newChildren[0] = `${nbValues} Aggregators Selected`;
    }

    if (!hasValue) {
        return (
            <components.ValueContainer {...props}>
                {children}
            </components.ValueContainer>
        );
    }
    return (
        <components.ValueContainer {...props}>
            {newChildren}
        </components.ValueContainer>
    );
};

const Option = props => {
    const { data, isSelected } = props;
    data.checked = isSelected;
    const dataOptions = [data];
    return (
        <components.Option {...props}>
            <div className={data.value}>
                <FormElems.Checkbox
                    name={data.value}
                    options={dataOptions || []}
                    valueField="value"
                    labelField="label"
                    checked={isSelected}
                />
            </div>
        </components.Option>
    );
};

const SettlementActions = ({
    data,
    fetchData,
    resetPage,
    actions,
    userLevel,
    permissions,
    auth,
    callbackFunction,
}) => {
    const [showFilter, setShowFilter] = useState(false);
    const [actionType, setActionType] = useState(null);

    const handelCallbackFunction = () => {
        setShowFilter(false);
        callbackFunction();
    };

    const handleOpenActionPopup = actionType => {
        setShowFilter(true);
        setActionType(actionType);
    };
    const { original = {} } = data || {};
    const { originalData = {} } = original || {};

    const handleCloseOpenActionPopup = isFetch => {
        setShowFilter(false);
        setActionType(null);
        if (isFetch && !isFetch.target) {
            fetchData({
                pageIndex: 1,
                pageSize: 10,
            });
            resetPage();
        }
    };

    const generateReport = () => {
        const req = {
            reportFileName: `MSR_SETTLEMENT_${originalData.settlementId}`,
            reportType: 'MSR',
            msrReportMetadata: {
                entityID: originalData.entityId,
                entityName: originalData.entityName,
                settlementAcc: originalData.settlementAcc,
                settlementDate: moment(originalData.date).format(
                    'DD-MMMM-YYYY'
                ),
                entityGSTIN: '',
                entityState: '',
                utr: originalData.utr,
                payableEntityId: [originalData.payableEntityId],
                settlementId: originalData.settlementId,
                payableType: originalData?.payableType,
            },
        };
        // reusing txn saga for data check txn store
        actions.generateReportRequest(req);
    };

    return (
        <div className="userAction multiItem">
            <button className="view" onClick={handleOpenActionPopup} />

            {originalData.settlementStatus === 'SETTLED' && (
                <button
                    className="download"
                    onClick={generateReport}
                    disabled={originalData.instantSettlement}
                />
            )}

            <Popup
                title={original.entityName}
                actionType={actionType}
                showCloseIcon
                showOverlay
                showFilter={showFilter}
                data={data.original}
                closePopup={handleCloseOpenActionPopup}
            >
                <SettlementForm
                    actionType={actionType}
                    data={data.original}
                    handleOpenActionPopup={handleOpenActionPopup}
                    closePopup={handleCloseOpenActionPopup}
                    actions={actions}
                    userLevel={userLevel}
                    permissions={permissions}
                    comments={original.comments}
                    userId={auth?.userDetails?.userId}
                    callbackFunction={handelCallbackFunction}
                />
            </Popup>
        </div>
    );
};

// eslint-disable-next-line max-statements
const Settlement = props => {
    const {
        settlementData,
        actions,
        loader: { showLoader },
        transactionData,
        merchantData,
        auth,
    } = props;
    const {
        error,
        tableData,
        isLoading,
        pagination,
        dailySettlementMetrics,
    } = settlementData;

    const { entityList } = merchantData;
    const [prevDateValue, setPrevDateValue] = useState('');
    const [isResetDate, setIsResetDate] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const { activeReports, generatedFile } = transactionData || {};
    const selectedAggregator = useRef(null);
    const aggregatorInitialized = useRef(false);

    const { userLevel, associatedEntityId } = auth.userDetails;

    const { permissions } = auth.resourceDetails;


    const [merchantOptions, setMerchantOptions] = useState([]);
    const [merchantOptionsID, setMerchantOptionsID] = useState([]);
    const [selectedMerchant, setSelectedMerchant] = useState([]);
    const [selectedMerchantID, setSelectedMerchantID] = useState([]);
    const [showDownloadPopup, setShowDownloadPopup] = useState(false);
    const [searchKey, setSearchKey] = useState('TRANSACTION_ID');
    const [searchValue, setSearchValue] = useState('');


    const columns = useMemo(
        () => [
            { header: 'Merchant Name', accessor: 'entityName' },
            { header: 'Settlement ID', accessor: 'settlementId' },
            { header: 'Date/Time', accessor: 'date' },
            { header: 'UTR', accessor: 'utr' },
            {
                header: 'Net Settlement Amount',
                accessor: 'netSettlementAmount',
            },
            { header: 'Status', accessor: 'settlementStatus' },
            { header: 'Action', accessor: 'action' },
        ],
        []
    );

    const pageSizeOptions = [10, 20, 40, 60];

    // We'll start our table without any data
    const [pageCount, setPageCount] = useState(0);
    const [isResetPage, setIsResetPage] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [filterData, setFilterData] = useState({
        startDate: null,
        endDate: null,
    });

    const handleSearchEnd = (value, entityType) => {
        const type =
            entityType === 'MERCHANT_NAME' || entityType === 'MERCHANT_ID'
                ? 'MERCHANT'
                : entityType;

        const data = {
            entityName: ['MERCHANT_NAME', 'AGGREGATOR'].includes(entityType)
                ? value
                : '',
            entityCode: !['MERCHANT_NAME', 'AGGREGATOR'].includes(entityType)
                ? value
                : '',
        };
        actions.fetchEntityListRequest(type, data);
    };

    const fetchData = useCallback(
        ({ pageSize, pageIndex }) => {
            if (userLevel && filterData.startDate && filterData.endDate) {
                const request = {
                    pageSize,
                    pageNo: pageIndex,
                    ...filterData,
                };

                if (userLevel === 'MERCHANT') {
                    request.entityIds = [associatedEntityId];
                }

                actions.fetchSettlementRequest(request);
                setPageSize(pageSize);
                setSearchValue('');
            }
        },
        [actions, filterData, userLevel, associatedEntityId]
    );

    useEffect(() => {
        // This is a simple fix to call fetchData on page refresh after getting userLevel
        setFilterData(preData => ({ ...preData, reload: !preData.reload }));
    }, [userLevel, associatedEntityId]);

    const handleCloseViewDownloads = () => {
        setShowFilter(false);
    };
    const handleViewDownloads = () => {
        setShowFilter(true);
    };

    const handleSearchSubmit = searchValue => {
        actions.fetchSettlementRequest({
            searchKey: searchValue ? searchKey : null,
            searchValue: searchValue,
        });
    };

    useEffect(() => {
        setPageCount(Math.ceil(pagination.totalNumberOfRecords / pageSize));
    }, [pageSize, pagination.totalNumberOfRecords]);

    useEffect(() => {
        if (associatedEntityId && userLevel === 'MERCHANT') {
            actions.fetchAvailableBalanceRequest({
                entityId: associatedEntityId,
            });
            actions.fetchNetSettlementRequest({
                entityId: associatedEntityId,
            });
        }
    }, [actions, associatedEntityId, userLevel]);

    useEffect(() => {
        if (generatedFile.isSuccess) {
            setShowFilter(true);
        }
        return () => {
            actions.generateReportError({ error: {} });
        };
    }, [actions, generatedFile.isSuccess]);

    // reset txn state if report is generated
    useEffect(() => {
        return () => {
            actions.resetTxnState();
        };
    }, [actions]);

    const handleAggregatorSelect = useCallback(
        entityCode => {
            selectedAggregator.current = entityCode;
            if (!entityCode) {
                // used to rerender so all merchant visible
                setFilterData({
                    createdOn: filterData.createdOn,
                });
                return;
            }
            actions.fetchEntityHierarchyRequest(
                entityCode,
                'LOWER_COMPLETE',
                'AGGREGATOR_TXN'
            );
        },
        [actions, filterData.createdOn]
    );

    useEffect(() => {
        // get all aggregator
        actions.fetchEntityListRequest(ENTITY_TYPE.AGGREGATOR);
        actions.fetchEntityListRequest(ENTITY_TYPE.MERCHANT);
    }, [actions])

    useEffect(() => {
        filterFieldList.forEach(filter => {
            if (filter.name !== 'aggregatorId') return;

            if (userLevel === 'AGGREGATOR' && entityList?.aggregator?.length > 0) {
                const autoSelected = {
                    labelField: entityList?.aggregator[0]?.name,
                    valueField: entityList?.aggregator[0]?.entityCode,
                };

                Object.assign(filter.props, {
                    isDisabled: true,
                    autoSelected: autoSelected
                });

                selectedAggregator.current = autoSelected.valueField;

                // ✅ Only fetch if not already done
                if (!aggregatorInitialized.current) {
                    aggregatorInitialized.current = true;
                    actions.fetchEntityHierarchyRequest(
                        autoSelected.valueField,
                        'LOWER_COMPLETE',
                        'AGGREGATOR_TXN'
                    );
                }
            } else {
                filter.props.onChange = handleAggregatorSelect;
            }
        });
    }, [actions, handleAggregatorSelect, entityList, userLevel]);

    useEffect(() => {
        const formatMerchant = formatOptions(entityList.merchant) || [];
        const formatMerchantIDs = formatOptionsID(entityList.merchant) || [];
        setMerchantOptions(formatMerchant);
        setMerchantOptionsID(formatMerchantIDs);
    }, [entityList]);

    const onInputChange = value => {
        const filtered =
            entityList.merchant.filter(item => {
                return item.name.toLowerCase().includes(value.toLowerCase());
            }) || [];

        const data = {
            entityName: value,
            entityCode: '',
        };

        if (filtered.length === 0 && value.length >= 3) {
            actions.fetchEntityListRequest(ENTITY_TYPE.MERCHANT, data);
        }
    };

    const onInputChangeID = value => {
        const filtered =
            entityList.merchant.filter(item => {
                return item.entityCode
                    .toLowerCase()
                    .includes(value.toLowerCase());
            }) || [];

        const data = {
            entityName: '',
            entityCode: value,
        };

        if (filtered.length === 0 && value.length >= 3) {
            actions.fetchEntityListRequest(ENTITY_TYPE.MERCHANT, data);
        }
    };

    const filterSubmitHandler = filterValues => {
        const {
            aggregatorId,
            merchantId = [],
            merchantName = [],
        } = filterValues;

        console.log(
            aggregatorId,
            merchantId,
            merchantName,
            'aggregatorId, merchantId, merchantName'
        );

        delete filterValues.merchantIds;
        const entityIds = [...merchantId, ...merchantName];
        if (aggregatorId) {
            entityIds.push(aggregatorId);
        }

        // REMOVE DUPLICATES AS MERCHANTS ID AND MERCHANTS NAME CAN BE SAME
        filterValues.entityIds = [...new Set(entityIds)];

        if (filterValues.createdOn) {
            filterValues.startDate = filterValues.createdOn.startDate;
            filterValues.endDate = filterValues.createdOn.endDate;
            delete filterValues.createdOn;
        }

        setFilterData({ ...filterValues, isLive: filterData.isLive });
        setIsResetPage(true);
    };

    const setAggregatorFilter = (fieldsList, entityList, handleSearchEnd) => {
        const { aggregator = [] } = entityList;
        console.log(entityList, 'entityList');
        if (aggregator.length) {
            fieldsList.forEach(element => {
                if (element.name === 'aggregatorId') {
                    element.props.data = aggregator;
                    element.props.handleSearchEnd = handleSearchEnd;
                }
            });
            return fieldsList;
        }
        return fieldsList;
    };

    const setMerchantFilter = (
        fieldsList,
        underlyingMerchant,
        entityList,
        selectedAggregator,
        handleSearchEnd
    ) => {
        const { merchants = [] } = entityList;
        if (selectedAggregator) {
            fieldsList.forEach(element => {
                if (
                    element.name === 'merchantId' ||
                    element.name === 'merchantName'
                ) {
                    element.props.data = merchants;
                    element.props.handleSearchEnd = handleSearchEnd;
                }
            });
            return fieldsList;
        }

        if (underlyingMerchant.length) {
            fieldsList.forEach(element => {
                if (
                    element.name === 'merchantId' ||
                    element.name === 'merchantName'
                ) {
                    element.props.data = underlyingMerchant;
                    element.props.handleSearchEnd = handleSearchEnd;
                }
            });
            return fieldsList;
        }
        return fieldsList;
    };

    let filters = setMerchantFilter(
        filterFieldList,
        entityList.merchant || [],
        entityList,
        selectedAggregator.current,
        handleSearchEnd
    );

    filters = setAggregatorFilter(filterFieldList, entityList, handleSearchEnd);

    const getFieldsList = () => {
        return userLevel === 'MERCHANT'
            ? filterFieldList.filter(
                item =>
                    item.name !== 'merchantId' &&
                      item.name !== 'aggregatorId' &&
                      item.name !== 'merchantName'
            )
            : filterFieldList;
    };

    const IS_EDI_REPORT =
        filterData && filterData.settlementMode === 'EDI_TRANSACTION';

    // Used to filter aggregator ID from entity IDs  
    const getFilterData = (filterData) => {
        if (filterData?.aggregatorId && Array.isArray(filterData?.entityIds)) {
            filterData.entityIds = filterData.entityIds.filter(
                entityID => entityID !== filterData.aggregatorId
            );
        }
        return filterData;
    };
    
    return (
        <div className="settlement-wrapper">
            <Table
                emptyTitle={ filterData.settlementMode === 'EDI_TRANSACTION' ? 'Report generated' : null}
                emptyDescription={ filterData.settlementMode === 'EDI_TRANSACTION' ? 'Please click on the download button to export the report' : null}
                tableTitle="Settlement"
                filterActionTitle="View Downloads"
                isFiltersEnable
                columns={columns}
                isPaginationEnable
                data={tableData}
                filterData={filterData}
                error={error && error.errorMessage}
                loading={showLoader || isLoading}
                pageCount={pageCount}
                fetchData={fetchData}
                numberOfRecord={pagination.totalNumberOfRecords}
                pageSizeOptions={pageSizeOptions}
                onClickTableAction={handleViewDownloads}
                paginationLabel="Rows per page"
                tableSummary={
                    <CurrentDaySummary
                        data={dailySettlementMetrics}
                        actions={actions}
                        tableData={tableData}
                        userLevel={userLevel}
                    />
                }
                pagePerView={3}
                isResetPage={isResetPage}
                afterResetPage={() => setIsResetPage(false)}
                additionalActions={() => {
                    return (
                        <DownloadFile
                            filterData={getFilterData(filterData)}
                            tableData={tableData}
                            actions={actions}
                            showDownloadPopup={showDownloadPopup}
                            setShowDownloadPopup={setShowDownloadPopup}
                            generateData={generatedFile}
                            isDateRangeFieldRequired
                            dateRangeFieldName="entityIds"
                            userLevel={userLevel}
                            auth={auth}
                        />
                    );
                }}
                tableActionPopup={
                    <Popup
                        title="Download Status"
                        showCloseIcon
                        showOverlay
                        showFilter={showFilter}
                        closePopup={handleCloseViewDownloads}
                    >
                        <ViewDownloadStatus
                            data={activeReports.reports}
                            reportType={IS_EDI_REPORT ? 'EDI_REPORT' : UNIFIED_MSR}
                        />
                    </Popup>
                }
                userActions={data => (
                    <SettlementActions
                        data={data}
                        fetchData={fetchData}
                        resetPage={() => setIsResetPage(true)}
                        actions={actions}
                        userLevel={userLevel}
                        permissions={permissions}
                    />
                )}
                tableFilters={
                    <TableFilter
                        customFilterClass="rightSkewed"
                        showOverlay
                        maximumNights={30}
                        fieldsList={getFieldsList()}
                        onSubmit={filterValues => {
                            filterSubmitHandler(filterValues);
                        }}
                        onReset={resetValue => {
                            if (resetValue.createdOn) {
                                resetValue.startDate =
                                    resetValue.createdOn.startDate;
                                resetValue.endDate =
                                    resetValue.createdOn.endDate;
                                delete resetValue.createdOn;
                            }
                            setFilterData({
                                isLive: filterData.isLive,
                                ...resetValue,
                            });
                            selectedAggregator.current = null;
                            setIsResetPage(true);
                        }}
                    />
                }
                tableCard={
                    userLevel === 'MERCHANT' &&
                    settlementData.availableBalance.odsFlag ? (
                            <OdsCard
                                auth={auth}
                                settlementData={settlementData}
                                fetchData={fetchData}
                            />
                        ) : null
                }
                tableSearch={
                    <div className="search custom-search-wrapper">
                        <FormElems.Select
                            name="searchKey"
                            placeholder="Select Search Key"
                            id="disbursement-cycle"
                            wrapperClass="settlement-type-field"
                            data={searchKeyList}
                            labelField="name"
                            valueField="value"
                            value={searchKey}
                            handleChange={val => setSearchKey(val)}
                        />
                        <TableSearch
                            search={searchValue}
                            setSearch={setSearchValue}
                            handleSearchSubmit={handleSearchSubmit}
                            placeholder="Enter Search Value"
                        />
                    </div>
                }
            />
        </div>
    );
};

const mapStateToProps = ({
    auth,
    settlementData,
    loader,
    staticData,
    transactionData,
    merchantData,
}) => ({
    auth,
    settlementData,
    loader,
    staticData,
    transactionData,
    merchantData,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            fetchSettlementRequest,
            fetchAuditTrailStaticRequest,
            fetchSettlementMetricsRequest,
            generateReportRequest,
            generateReportError,
            resetTxnState,
            retrySettlementRequest,
            fetchEntityListRequest,
            retriggerSettlementRequest,
            fetchAvailableBalanceRequest,
            fetchEntityHierarchyRequest,
            getMerchantSectionInfoRequest,
            fetchNetSettlementRequest,
        },
        dispatch
    ),
});

Settlement.propTypes = {
    actions: PropTypes.object,
    auth: PropTypes.object,
    data: PropTypes.object,
    staticData: PropTypes.object,
    settlementData: PropTypes.object,
    transactionData: PropTypes.object,
    loader: PropTypes.object,
    merchantData: PropTypes.object,
};

SettlementActions.propTypes = {
    data: PropTypes.object,
    actions: PropTypes.object,
    roleStatus: PropTypes.string,
    userLevel: PropTypes.string,
    fetchData: PropTypes.func,
    resetPage: PropTypes.func,
    permissions: PropTypes.object,
    auth: PropTypes.object,
    callbackFunction: PropTypes.func,
};

Option.propTypes = {
    data: PropTypes.object,
    selectProps: PropTypes.object,
    isSelected: PropTypes.bool,
    hasValue: PropTypes.bool,
};

ValueContainer.propTypes = {
    hasValue: PropTypes.bool,
    children: PropTypes.array,
    getValue: PropTypes.func,
    options: PropTypes.array,
};

export default connect(mapStateToProps, mapDispatchToProps)(Settlement);
